import type { PartialPropsOf, PropsOf, ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentActionBar } from "~/components/content/content-action-bar";
import { ContentAuthorLink } from "~/components/content/content-author-link";
import { ContentCoverImage } from "~/components/content/content-cover-image";
import { ContentHeadline } from "~/components/content/content-headline";
import { ContentLiveTag } from "~/components/content/content-live-tag";
import { ContentPrintHeadline } from "~/components/content/content-print-headline";
import { useContentSponsor } from "~/components/content/content-sponsor-tag/hooks";
import { ContentSubHeadline } from "~/components/content/content-sub-headline";
import { ContentSummary } from "~/components/content/content-summary";
import { ContentTopicLink } from "~/components/content/content-topic-link";
import { SponsorTag } from "~/components/sponsor-tag";
import { VideoDurationOverlay } from "~/components/video/video-duration-overlay";
import type { contentItemRenderContent$key } from "~/queries/__generated__/contentItemRenderContent.graphql";

import { ContentItemCard } from "./variants/card";
import { ContentItemMorningStudio } from "./variants/morning-studio";
import { ContentItemOpinion } from "./variants/opinion";
import { ContentItemPrimary } from "./variants/primary";
import { ContentItemSearch } from "./variants/search";
import { ContentItemSecondary } from "./variants/secondary";

type Props = {
  className?: string;
  reference: contentItemRenderContent$key;
  variant: Variant;
  withAuthorLink?: boolean;
  withTopicLink?: Omit<PropsOf<typeof ContentTopicLink>, "reference">;
};

type Variant = "primary" | "secondary" | "search" | "opinion" | "card" | "morning-studio";

export type DefaultComponentsProvider = {
  actionBar: (props?: PartialPropsOf<typeof ContentActionBar>) => ReactNode;
  authorLink?: (props?: PartialPropsOf<typeof ContentAuthorLink>) => ReactNode;
  coverImage: (props?: PartialPropsOf<typeof ContentCoverImage>) => ReactNode;
  headline: (props?: PartialPropsOf<typeof ContentHeadline>) => ReactNode;
  liveTag: (props?: PartialPropsOf<typeof ContentLiveTag>) => ReactNode;
  printHeadline: (props?: PartialPropsOf<typeof ContentPrintHeadline>) => ReactNode;
  sponsorTag?: (props?: PartialPropsOf<typeof SponsorTag>) => ReactNode;
  subHeadline: (props?: PartialPropsOf<typeof ContentSubHeadline>) => ReactNode;
  summary: (props?: PartialPropsOf<typeof ContentSummary>) => ReactNode;
  topicLink?: (props?: PartialPropsOf<typeof ContentTopicLink>) => ReactNode;
  video?: {
    durationOverlay: (props?: PartialPropsOf<typeof VideoDurationOverlay>) => ReactNode;
  };
};

export const ContentItemRender = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_, variant, withAuthorLink, withTopicLink }, reference) => {
    const content = useFragment(
      graphql`
        fragment contentItemRenderContent on Content {
          ...entityLink
          ...contentCoverImageContent
          ...contentActionBarContent
          ...contentSubHeadlineContent
          ...contentSummaryContent
          ...contentHeadlineContent
          ...contentPrintHeadlineContent
          ...contentTopicLinkContent
          ...contentLiveTagContent
          ...contentAuthorLinkContent
          ...primaryContentItemContent
          ...secondaryContentItemContent
          ...searchContentItemContent
          ...opinionContentItemContent
          ...cardContentItemContent
          ...morningStudioContentItemContent
          ...hooksUseContentSponsorContent
          ... on Video {
            __typename
            ...videoDurationOverlayVideo
          }
        }
      `,
      reference_,
    );
    const contentSponsor = useContentSponsor(content);
    const provider: DefaultComponentsProvider = {
      actionBar: props => <ContentActionBar reference={content} {...props} />,
      coverImage: props => (
        <ContentCoverImage
          reference={content}
          responsiveVariants={{
            desktopUp: "size540x360",
            mobileUp: "size540x360",
            tabletUp: "size540x360",
          }}
          {...props}
        />
      ),
      headline: props => (
        <ContentHeadline
          reference={content}
          useContentSponsorStyle={!!contentSponsor?.name}
          {...props}
        />
      ),
      liveTag: props => <ContentLiveTag reference={content} {...props} />,
      printHeadline: props => <ContentPrintHeadline reference={content} {...props} />,
      subHeadline: props => <ContentSubHeadline reference={content} {...props} />,
      summary: props => <ContentSummary reference={content} {...props} />,
    };
    if (withAuthorLink) {
      provider.authorLink = props => <ContentAuthorLink reference={content} {...props} />;
    }
    if (withTopicLink) {
      provider.topicLink = props => (
        <ContentTopicLink {...withTopicLink} {...props} reference={content} />
      );
    }
    if (content.__typename === "Video") {
      provider.video = {
        durationOverlay: props => (
          <VideoDurationOverlay
            reference={content}
            responsiveVariants={{ desktopUp: "medium", mobileUp: "small", tabletUp: "medium" }}
            {...props}
          />
        ),
      };
    }

    if (contentSponsor?.name) {
      provider.sponsorTag = props => <SponsorTag reference={contentSponsor} {...props} />;
    }

    switch (variant) {
      case "primary":
        return (
          <ContentItemPrimary
            className={className}
            provider={provider}
            ref={reference}
            reference={content}
          />
        );
      case "secondary":
        return (
          <ContentItemSecondary
            className={className}
            provider={provider}
            ref={reference}
            reference={content}
          />
        );
      case "opinion":
        return (
          <ContentItemOpinion
            className={className}
            provider={provider}
            ref={reference}
            reference={content}
          />
        );
      case "search":
        return (
          <ContentItemSearch
            className={className}
            provider={provider}
            ref={reference}
            reference={content}
          />
        );
      case "morning-studio":
        return (
          <ContentItemMorningStudio
            className={className}
            provider={provider}
            ref={reference}
            reference={content}
          />
        );
      case "card":
        return (
          <ContentItemCard
            className={className}
            provider={provider}
            ref={reference}
            reference={content}
          />
        );
    }
  },
);

ContentItemRender.displayName = "ContentItemRender";
